import { useContext } from "react";
import React from "react";
import { Container, Button, Offcanvas } from "react-bootstrap";
import { Col, Row, Table } from "react-bootstrap";
import Loading from "../Loading";
import { RebalanceContext } from "./RebalanceInfo";
import { useAuthenticatedFetch } from "../lib/api";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import RebalanceAccountInsights, {
  RebalanceAccountWithMetrics,
} from "./RebalanceAccountInsights";

interface AccountSettings {
  account_type: string;
  tax_type: "tax_exempt" | "taxable";
  gains_budget_lt: number | null;
  gains_budget_st: number | null;
  wash_sale_treatment: "avoid_wash_sales";
}

interface Account {
  account_id: number;
  bridge_account_id: number;
  account_name: string;
  rebalance_type: "tolerance";
  is_rebalance_account: boolean;
  raise_spend_cash_amount: number | null;
  settings: AccountSettings;
  model_id: number;
  model: {
    primary: Record<string, number>;
    secondary: Record<string, Record<string, Record<string, number>>>;
  } | null;
  security_restrictions: Record<string, unknown>;
  cash_settings: {
    target_cash_type: "absolute";
    target_cash_weight: number | null;
    target_cash_dollar: number | null;
    minimum_cash_trigger_weight: number | null;
    minimum_cash_trigger_dollar: number | null;
    minimum_cash_trigger_condition: "AND";
    maximum_cash_trigger_weight: number | null;
    maximum_cash_trigger_dollar: number | null;
    maximum_cash_trigger_condition: "AND";
  };
  risk_score: number | null;
  balance: number | null;
  cash_balance: number | null;
  holdings: null;
}

interface Model {
  primary: Record<string, number>;
  secondary: Record<string, Record<string, Record<string, number>>>;
}

interface SharedRebalanceData {
  accounts: Record<string, Account>;
  householdModel: Model | null;
  constrainedAccounts?: Record<
    string,
    {
      constrained_target: Record<string, number>;
      securities_map: Record<
        string,
        {
          target: number;
          constrained: number;
          risk: string;
          asset_class: string;
          adjusted_target?: number;
          adjusted_allocation?: number;
        }
      >;
    }
  >;
  trades?: Record<
    string,
    Record<
      string,
      {
        shares: number;
        price: number;
        realized_gain_loss_st: number;
        realized_gain_loss_lt: number;
        trade_weight: number | null;
        remaining_trade_value: number | null;
        remaining_shares_available_to_sell: number | null;
      }
    >
  >;
}
interface AccountLinkProps {
  accountId: string | number;
  accountName: string | null;
}

const AccountLink: React.FC<AccountLinkProps> = ({
  accountId,
  accountName,
}) => {
  if (!accountId) return null;

  return (
    <>
      <a
        href={`/clients/accounts/${accountId}/overview`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {accountName}
      </a>
    </>
  );
};

interface HoldingDict {
  [symbol: string]: {
    shares: number;
    value: number;
    is_bond: boolean;
    price: number;
    weight: number;
    weight_ex_cash: number | null;
    lots: {
      security_value: number;
      lots: any[];
    };
  };
}

const PortfolioCol = ({
  title,
  accountId,
  accountName,
  data,
}: {
  title: string;
  accountId: string;
  accountName: string;
  data: Record<string, number> | null;
}) => (
  <Col md={6}>
    <div>
      <h5>{title}</h5>
      <Table>
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Target Allocation</th>
          </tr>
        </thead>
        <tbody>
          {data &&
            Object.entries(data).map(([symbol, allocation]) => (
              <tr key={symbol}>
                <td>{symbol}</td>
                <td>{allocation}</td>
              </tr>
            ))}
          <tr className="fw-bold">
            <td>Total</td>
            <td>
              {data &&
                Object.values(data).reduce(
                  (sum, allocation) => sum + allocation,
                  0,
                )}
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  </Col>
);

interface TargetPortfolioRowProps {
  explainData: {
    [accountId: string]: {
      starting_holdings: HoldingDict;
      initial_target_portfolios_by_account: Record<string, number>;
      restricted_securities_by_account: Record<string, Record<string, unknown>>;
      target_portfolio: Model;
      post_process_restriction: Model;
      post_secondary_target: Record<string, number>;
      post_restriction_hints?: string[];
      post_secondary_hints?: string[];
      constrained_accounts?: Record<string, Record<string, number>>;
    };
  };
  sharedData: SharedRebalanceData;
}

export const TargetPortfolioRow = ({
  explainData,
  sharedData,
}: TargetPortfolioRowProps) => {
  const { rebalance } = useContext(RebalanceContext);

  return (
    <>
      <Row>
        <h3>Target Allocations</h3>
      </Row>
      {Object.entries(explainData).map(([accountId, account]) => {
        // Find the corresponding account from the rebalance data
        const rebalanceAccount = rebalance?.accounts?.find(
          (acc) => acc.id === parseInt(accountId),
        );

        return (
          <Container key={accountId} className="px-0 mb-4">
            <Row>
              <Col md={12} className="px-0">
                {/* Add RebalanceAccountInsights above the table */}
                {rebalanceAccount && "totalBalance" in rebalanceAccount && (
                  <div className="mb-4">
                    <RebalanceAccountInsights
                      rebalanceAccount={
                        rebalanceAccount as RebalanceAccountWithMetrics
                      }
                      rebalanceType={rebalance?.type || "tolerance"}
                    />
                  </div>
                )}

                <Table style={{ fontSize: "0.875rem" }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "0.25rem" }}>Risk</th>
                      <th style={{ padding: "0.25rem" }}>Asset Class</th>
                      <th style={{ padding: "0.25rem" }}>Symbol</th>
                      {account.starting_holdings && (
                        <th
                          colSpan={2}
                          style={{ textAlign: "center", padding: "0.25rem" }}
                        >
                          Holdings
                        </th>
                      )}
                      <th style={{ padding: "0.25rem" }}>Target w. Cash</th>
                      <th style={{ padding: "0.25rem" }}>Security Group</th>
                      <th style={{ padding: "0.25rem" }}>Adjusted Target</th>
                      <th style={{ padding: "0.25rem", textAlign: "right" }}>
                        Change{" "}
                        <span style={{ fontSize: "0.8em", color: "#aaa" }}>
                          (Δ)
                        </span>
                      </th>
                      <th style={{ padding: "0.25rem", textAlign: "right" }}>
                        Shares{" "}
                        <span style={{ fontSize: "0.8em", color: "#aaa" }}>
                          (Δ)
                        </span>
                      </th>
                      <th style={{ padding: "0.25rem", textAlign: "right" }}>
                        Amount{" "}
                        <span style={{ fontSize: "0.8em", color: "#aaa" }}>
                          (Δ)
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.target_portfolio &&
                      account.post_process_restriction &&
                      account.post_secondary_target &&
                      Object.keys({
                        ...account.target_portfolio.primary,
                        ...account.post_process_restriction.primary,
                        ...account.post_secondary_target,
                        ...(account.starting_holdings || {}),
                      })
                        .sort((symbolA, symbolB) => {
                          const securityA =
                            sharedData.constrainedAccounts?.[accountId]
                              ?.securities_map?.[symbolA];
                          const securityB =
                            sharedData.constrainedAccounts?.[accountId]
                              ?.securities_map?.[symbolB];

                          if (securityA && securityB) {
                            // First sort by Risk
                            if (securityA.risk < securityB.risk) return -1;
                            if (securityA.risk > securityB.risk) return 1;

                            // If Risk is the same, sort by Asset Class
                            if (securityA.asset_class < securityB.asset_class)
                              return -1;
                            if (securityA.asset_class > securityB.asset_class)
                              return 1;
                          }

                          // If Risk and Asset Class are the same, or if securities data is missing, sort by Symbol
                          if (symbolA < symbolB) return -1;
                          if (symbolA > symbolB) return 1;
                          return 0;
                        })
                        .map((symbol) => (
                          <tr key={symbol}>
                            <td style={{ padding: "0.25rem" }}>
                              {sharedData.constrainedAccounts &&
                              sharedData.constrainedAccounts[accountId] &&
                              sharedData.constrainedAccounts[accountId]
                                .securities_map &&
                              sharedData.constrainedAccounts[accountId]
                                .securities_map[symbol]
                                ? sharedData.constrainedAccounts[accountId]
                                    .securities_map[symbol].risk
                                : "—"}
                            </td>
                            <td style={{ padding: "0.25rem" }}>
                              {sharedData.constrainedAccounts &&
                              sharedData.constrainedAccounts[accountId] &&
                              sharedData.constrainedAccounts[accountId]
                                .securities_map &&
                              sharedData.constrainedAccounts[accountId]
                                .securities_map[symbol]
                                ? sharedData.constrainedAccounts[accountId]
                                    .securities_map[symbol].asset_class
                                : "—"}
                            </td>
                            <td style={{ padding: "0.25rem" }}>{symbol}</td>
                            {account.starting_holdings && (
                              <>
                                <td
                                  style={{
                                    textAlign: "right",
                                    padding: "0.25rem",
                                  }}
                                >
                                  {account.starting_holdings[symbol] !==
                                  undefined
                                    ? `$${account.starting_holdings[symbol].value.toLocaleString()}`
                                    : "—"}
                                </td>
                                <td style={{ padding: "0.25rem" }}>
                                  {account.starting_holdings[symbol] !==
                                  undefined
                                    ? account.starting_holdings[symbol].weight
                                    : "—"}
                                </td>
                              </>
                            )}
                            <td style={{ padding: "0.25rem" }}>
                              {account.target_portfolio.primary[symbol] !==
                              undefined
                                ? account.target_portfolio.primary[symbol]
                                : "—"}
                            </td>
                            <td style={{ padding: "0.25rem" }}>
                              {account.post_secondary_target[symbol] !==
                              undefined
                                ? account.post_secondary_target[symbol]
                                : "—"}
                            </td>
                            <td style={{ padding: "0.25rem" }}>
                              {sharedData.constrainedAccounts &&
                              sharedData.constrainedAccounts[accountId] &&
                              sharedData.constrainedAccounts[accountId]
                                .constrained_target &&
                              symbol in
                                sharedData.constrainedAccounts[accountId]
                                  .constrained_target
                                ? sharedData.constrainedAccounts[accountId]
                                    .constrained_target[symbol] === 0
                                  ? "—"
                                  : sharedData.constrainedAccounts[accountId]
                                      .constrained_target[symbol]
                                : "—"}
                            </td>
                            <td
                              style={{ padding: "0.25rem", textAlign: "right" }}
                            >
                              {(() => {
                                const holdingWeight =
                                  account.starting_holdings?.[symbol]?.weight ??
                                  0;
                                const constrainedValue =
                                  sharedData.constrainedAccounts?.[accountId]
                                    ?.constrained_target?.[symbol] ?? 0;

                                return (
                                  constrainedValue - holdingWeight
                                ).toFixed(4);
                              })()}
                            </td>
                            <td
                              style={{ padding: "0.25rem", textAlign: "right" }}
                            >
                              {sharedData.trades?.[accountId]?.[symbol]
                                ?.shares ?? "—"}
                            </td>
                            <td
                              style={{ padding: "0.25rem", textAlign: "right" }}
                            >
                              {(() => {
                                const trade =
                                  sharedData.trades?.[accountId]?.[symbol];
                                if (trade?.shares && trade?.price) {
                                  const amount = trade.shares * trade.price;
                                  return `$${amount.toLocaleString()}`;
                                }
                                return "—";
                              })()}
                            </td>
                          </tr>
                        ))}

                    <tr>
                      <td style={{ padding: "0.25rem" }}>—</td>
                      <td style={{ padding: "0.25rem" }}>—</td>
                      <td style={{ padding: "0.25rem" }}>Total</td>
                      {account.starting_holdings && (
                        <>
                          <td
                            style={{ textAlign: "right", padding: "0.25rem" }}
                          >
                            $
                            {Object.values(account.starting_holdings)
                              .reduce((sum, holding) => sum + holding.value, 0)
                              .toLocaleString()}
                          </td>
                          <td style={{ padding: "0.25rem" }}>
                            {Object.values(account.starting_holdings).reduce(
                              (sum, holding) => sum + (holding.weight || 0),
                              0,
                            )}
                          </td>
                        </>
                      )}
                      <td style={{ padding: "0.25rem" }}>
                        {account.target_portfolio
                          ? Object.values(
                              account.target_portfolio.primary,
                            ).reduce((sum, value) => sum + (value || 0), 0)
                          : "—"}
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        {account.post_secondary_target
                          ? Object.values(account.post_secondary_target).reduce(
                              (sum, value) => sum + (value || 0),
                              0,
                            )
                          : "—"}
                      </td>
                      <td style={{ padding: "0.25rem" }}>
                        {sharedData.constrainedAccounts &&
                        sharedData.constrainedAccounts[accountId] &&
                        sharedData.constrainedAccounts[accountId]
                          .constrained_target
                          ? (() => {
                              const total = Object.values(
                                sharedData.constrainedAccounts[accountId]
                                  .constrained_target,
                              ).reduce((sum, value) => sum + (value || 0), 0);
                              return total === 0 ? "—" : total;
                            })()
                          : "—"}
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "right" }}>
                        —
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "right" }}>
                        —
                      </td>
                      <td style={{ padding: "0.25rem", textAlign: "right" }}>
                        {(() => {
                          const totalAmount = sharedData.trades?.[accountId]
                            ? Object.values(
                                sharedData.trades[accountId],
                              ).reduce(
                                (sum, trade) =>
                                  sum +
                                  (trade.shares || 0) * (trade.price || 0),
                                0,
                              )
                            : 0;
                          return totalAmount === 0
                            ? "—"
                            : `$${totalAmount.toLocaleString()}`;
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                {(account.post_restriction_hints ||
                  account.post_secondary_hints) && (
                  <ul>
                    {account.post_restriction_hints &&
                      account.post_restriction_hints.map((hint, index) => (
                        <li key={index}>{hint}</li>
                      ))}
                    {account.post_secondary_hints &&
                      account.post_secondary_hints.map((hint, index) => (
                        <li key={index}>{hint}</li>
                      ))}
                  </ul>
                )}
              </Col>
            </Row>
          </Container>
        );
      })}
    </>
  );
};

interface TargetRiskAllocationRowProps {
  explainData: {
    account_target_risk_allocations: Record<string, Record<string, number>>;
    security_risk_allocations_by_account: Record<
      string,
      Record<string, Record<string, number | boolean>>
    >;
    allocations?: Record<
      string,
      {
        balance: number;
        composite_risk: number;
        risk_ratio: number;
        growth_dollar: number;
        unallocated_growth_dollar: number;
        growth_weight: number;
      }
    >;
  };
  sharedData: SharedRebalanceData;
}

interface AllocationTableProps {
  allocations: Record<
    string,
    {
      balance: number;
      composite_risk: number;
      risk_ratio: number;
      growth_dollar: number;
      unallocated_growth_dollar: number;
      growth_weight: number;
    }
  >;
  sharedData: SharedRebalanceData;
}

export const AllocationTable = ({
  allocations,
  sharedData,
}: AllocationTableProps) => (
  <>
    <Row>
      <h4>Account Allocations</h4>
    </Row>
    <Row>
      <Col>
        <Table striped bordered>
          <thead>
            <tr>
              <th>Account</th>
              <th>Balance ($)</th>
              <th>Composite Risk ($)</th>
              <th>Risk Ratio</th>
              <th>Growth ($)</th>
              <th>Unallocated Growth ($)</th>
              <th>Growth Weight</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(allocations).map(([accountId, allocation]) => {
              const accountName =
                accountId === "total"
                  ? "Total"
                  : (sharedData.accounts[accountId].account_name ?? accountId);

              return (
                <tr key={accountId}>
                  <td>
                    {accountId === "total" ? (
                      "Total"
                    ) : (
                      <AccountLink
                        accountId={accountId}
                        accountName={accountName}
                      />
                    )}
                  </td>
                  <td>${allocation.balance.toLocaleString()}</td>
                  <td>${allocation.composite_risk.toLocaleString()}</td>
                  <td>{allocation.risk_ratio}</td>
                  <td>${allocation.growth_dollar.toLocaleString()}</td>
                  <td>
                    ${allocation.unallocated_growth_dollar.toLocaleString()}
                  </td>
                  <td>{allocation.growth_weight}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Col>
    </Row>
  </>
);

export const TargetRiskAllocationRow = ({
  explainData,
  sharedData,
}: TargetRiskAllocationRowProps) => (
  <>
    {Object.entries(explainData.account_target_risk_allocations).map(
      ([accountId, allocations]) => {
        const accountName = sharedData.accounts[accountId].account_name;
        return (
          <div key={accountId}>
            <Row>
              <h4>
                {accountId && (
                  <AccountLink
                    accountId={accountId}
                    accountName={accountName}
                  />
                )}
              </h4>
            </Row>
            <Row>
              <Col md={4}>
                <h5>Account Risk Allocations</h5>
                <Table>
                  <thead>
                    <tr>
                      <th>Risk Category</th>
                      <th>Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(allocations).map(
                      ([category, allocation]) => (
                        <tr key={category}>
                          <td>{category}</td>
                          <td>{allocation}</td>
                        </tr>
                      ),
                    )}

                    <tr>
                      <td>Total</td>
                      <td>
                        {Object.values(allocations).reduce(
                          (sum, allocation) => sum + allocation,
                          0,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={8}>
                <h5>Security Risk Allocations</h5>
                <Table>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Growth</th>
                      <th>Defensive</th>
                      <th>Cash</th>
                      <th>Unknown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(
                      explainData.security_risk_allocations_by_account[
                        accountId
                      ],
                    ).map(([symbol, risks]) => (
                      <tr key={symbol}>
                        <td>{symbol}</td>
                        <td>{(risks.Growth as number) || 0}</td>
                        <td>{(risks.Defensive as number) || 0}</td>
                        <td>{(risks.Cash as number) || 0}</td>
                        <td>{(risks.Unknown as number) || 0}</td>
                      </tr>
                    ))}

                    <tr>
                      <td>Total</td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) => sum + ((risks.Growth as number) || 0),
                          0,
                        )}
                      </td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) =>
                            sum + ((risks.Defensive as number) || 0),
                          0,
                        )}
                      </td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) => sum + ((risks.Cash as number) || 0),
                          0,
                        )}
                      </td>
                      <td>
                        {Object.values(
                          explainData.security_risk_allocations_by_account[
                            accountId
                          ],
                        ).reduce(
                          (sum, risks) =>
                            sum + ((risks.Unknown as number) || 0),
                          0,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </div>
        );
      },
    )}

    {explainData.allocations && (
      <AllocationTable
        allocations={explainData.allocations}
        sharedData={sharedData}
      />
    )}
  </>
);

interface RebalanceRequestExplainStepProps {
  explainData: {
    as_of_date: string;
    household_model: {
      primary: Record<string, number>;
      secondary: Record<string, Record<string, Record<string, unknown>>>;
    };
    accounts: Record<string, Account>;
    category_mapping?: Record<string, Record<string, number>>;
  };
}

export const RebalanceRequestExplainStep = ({
  explainData,
}: RebalanceRequestExplainStepProps) => (
  <>
    <Container>
      {explainData.as_of_date && (
        <Row className="mb-3">
          <Col className="text-end">
            As of Date: {new Date(explainData.as_of_date).toLocaleDateString()}
          </Col>
        </Row>
      )}

      <AccountDetailsDrawer
        accounts={explainData.accounts}
        householdModel={explainData.household_model}
        categoryMapping={explainData.category_mapping}
      />
    </Container>
  </>
);

interface ModelCashExplainStepProps {
  explainData: {
    initial_model_cash_targets: Record<
      string,
      {
        primary: Record<string, number>;
        secondary: Record<string, Record<string, Record<string, unknown>>>;
      }
    >;
  };
  sharedData: SharedRebalanceData;
}

export const ModelCashExplainStep = ({
  explainData,
  sharedData,
}: ModelCashExplainStepProps) => (
  <>
    <Row>
      <h3>2. Model Cash</h3>
      <p>
        Adjusts weights based on cash settings. Uses account model or household
        model if account model is not available.
      </p>
    </Row>

    <Container>
      {Object.entries(explainData.initial_model_cash_targets).map(
        ([accountId, cash_target]) => {
          const accountName = sharedData.accounts[accountId].account_name;

          const model = sharedData.accounts[accountId].model
            ? sharedData.accounts[accountId].model
            : sharedData.householdModel;

          return (
            <div key={accountId}>
              <Row>
                <h4>
                  {accountId && (
                    <AccountLink
                      accountId={accountId}
                      accountName={accountName}
                    />
                  )}
                </h4>
              </Row>
              <Row>
                <Col md={6}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Target Portfolio</th>
                        <th>Model Cash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys({
                        ...(model?.primary || {}),
                        ...cash_target.primary,
                      }).map((symbol) => (
                        <tr key={symbol}>
                          <td>{symbol}</td>
                          <td>
                            {model?.primary?.[symbol] !== undefined
                              ? model.primary[symbol]
                              : "—"}
                          </td>
                          <td>
                            {cash_target.primary[symbol] !== undefined
                              ? cash_target.primary[symbol]
                              : "—"}
                          </td>
                        </tr>
                      ))}
                      <tr className="fw-bold">
                        <td>Total</td>
                        <td>
                          {model?.primary
                            ? Object.values(model.primary).reduce(
                                (sum, allocation) => sum + allocation,
                                0,
                              )
                            : "—"}
                        </td>
                        <td>
                          {Object.values(cash_target.primary).reduce(
                            (sum, allocation) => sum + allocation,
                            0,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          );
        },
      )}
    </Container>

    <Row>
      <h3>2. Taxable Target Portfolio</h3>
      <p>
        Holdings plus positive cash show. Create Target Portfolio after 1)
        security restrictions applied and 2) modified for secondary securities
        in the security groups.
      </p>
    </Row>
  </>
);

interface TargetFinalExplainStepProps {
  explainData: {
    account_final_target_portfolios: Record<string, Record<string, number>>;
  };
  sharedData: SharedRebalanceData;
}

export const TargetFinalExplainStep = ({
  explainData,
  sharedData,
}: TargetFinalExplainStepProps) => (
  <>
    <Row>
      <h3>Target - Final</h3>
    </Row>

    {Object.entries(explainData.account_final_target_portfolios).reduce(
      (rows, [account_id, securities], index, array) => {
        if (index % 2 === 0) {
          rows.push(
            <Row>
              <PortfolioCol
                title={`Target Portfolio - ${sharedData.accounts[account_id].account_name}`}
                accountId={account_id}
                accountName={
                  sharedData.accounts[account_id]?.settings.account_type ?? ""
                }
                data={securities}
              />
              {array[index + 1] && (
                <PortfolioCol
                  title={`Target Portfolio - ${sharedData.accounts[array[index + 1][0]]?.account_name}`}
                  accountId={array[index + 1][0]}
                  accountName={
                    sharedData.accounts[array[index + 1][0]]?.settings
                      .account_type ?? ""
                  }
                  data={array[index + 1][1]}
                />
              )}
            </Row>,
          );
        }
        return rows;
      },
      [] as JSX.Element[],
    )}
  </>
);

interface TaxableAccountRebalanceStepProps {
  explainData: {
    taxable_account_rebalance_results: Record<
      string,
      {
        account_id: number;
        household_target_portfolio: Record<string, number>;
        final_trades: Record<
          string,
          {
            shares: number;
            price: number;
            realized_gain_loss_st: number;
            realized_gain_loss_lt: number;
            trade_weight: number | null;
            remaining_trade_value: number | null;
            remaining_shares_available_to_sell: number | null;
          }
        >;
        gain_loss: {
          gain_realized: number;
          loss_realized: number;
        };
        taxable_risk_category: Record<string, number>;
        taxable_asset_category: Record<string, number>;
      }
    >;
    taxable_balances: number;
    taxable_growth_amount: number;
  };
  sharedData: SharedRebalanceData;
}

export const TaxableAccountRebalanceStep = ({
  explainData,
  sharedData,
}: TaxableAccountRebalanceStepProps) => (
  <>
    <Row>
      <h4>4. Taxable Account Rebalance</h4>
    </Row>

    <Container>
      <Row className="mb-3">
        <Col>
          <h5>
            Total Taxable Balance: $
            {explainData.taxable_balances.toLocaleString()}
          </h5>
          <h5>
            Total Growth Amount: $
            {explainData.taxable_growth_amount.toLocaleString()}
          </h5>
        </Col>
      </Row>

      {Object.entries(explainData.taxable_account_rebalance_results).map(
        ([accountId, account]) => {
          const accountName = sharedData.accounts[accountId].account_name;
          return (
            <div key={accountId}>
              <Row>
                <Col md={6}>
                  <h5>
                    Target Portfolio {accountId && `- Account(${accountId})`}
                  </h5>
                  <Table>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Target Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(account.household_target_portfolio).map(
                        ([symbol, allocation]) => (
                          <tr key={symbol}>
                            <td>{symbol}</td>
                            <td>{allocation}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </Col>

                <Col md={6}>
                  <h5>Final Trades {accountId && `- Account(${accountId})`}</h5>
                  <Table>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Shares</th>
                        <th>Price</th>
                        <th>ST Gain/Loss</th>
                        <th>LT Gain/Loss</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(account.final_trades).map(
                        ([symbol, trade]) => (
                          <tr key={symbol}>
                            <td>{symbol}</td>
                            <td>{trade.shares}</td>
                            <td>${trade.price.toFixed(2)}</td>
                            <td>${trade.realized_gain_loss_st.toFixed(2)}</td>
                            <td>${trade.realized_gain_loss_lt.toFixed(2)}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={6}>
                  <h5>
                    Risk Categories -
                    {accountId && (
                      <AccountLink
                        accountId={accountId}
                        accountName={accountName}
                      />
                    )}
                  </h5>
                  <Table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(account.taxable_risk_category).map(
                        ([category, allocation]) => (
                          <tr key={category}>
                            <td>{category}</td>
                            <td>{allocation}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </Col>

                <Col md={6}>
                  <h5>
                    Asset Categories -
                    {accountId && (
                      <AccountLink
                        accountId={accountId}
                        accountName={accountName}
                      />
                    )}
                  </h5>
                  <Table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(account.taxable_asset_category).map(
                        ([category, allocation]) => (
                          <tr key={category}>
                            <td>{category}</td>
                            <td>{allocation}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row className="mt-3 mb-4">
                <Col>
                  <h5>Realized Gains/Losses</h5>
                  <div>
                    Gains Realized: $
                    {account.gain_loss.gain_realized.toFixed(2)}
                  </div>
                  <div>
                    Losses Realized: $
                    {account.gain_loss.loss_realized.toFixed(2)}
                  </div>
                </Col>
              </Row>
            </div>
          );
        },
      )}
    </Container>
    <Row>
      <h3>5. Second Draft - Non-Taxable</h3>
    </Row>
  </>
);

const MissingStep = ({
  stepName,
  explainData,
}: {
  stepName: string;
  explainData: unknown;
}) => (
  <>
    <Row>
      <h3>Unknown Step: {stepName}</h3>
    </Row>
    <Container>
      <Row>
        <Col>
          <div>Step type '{stepName}' is not implemented yet</div>
          <pre>{JSON.stringify(explainData, null, 2)}</pre>
        </Col>
      </Row>
    </Container>
  </>
);

interface ConstrainedAccountsStepProps {
  explainData: Record<
    string,
    {
      constrained_target: Record<string, number>;
      securities_map: Record<
        string,
        {
          target: number;
          constrained: number;
          risk: string;
          asset_class: string;
          adjusted_target?: number;
          adjusted_allocation?: number;
        }
      >;
    }
  >;
  sharedData: SharedRebalanceData;
}

export const ConstrainedAccountsStep = ({
  explainData,
  sharedData,
}: ConstrainedAccountsStepProps) => {
  const copySecurityMapData = () => {
    const securityMapData = Object.fromEntries(
      Object.entries(explainData).map(([accountId, accountData]) => [
        accountId,
        accountData.securities_map,
      ]),
    );

    navigator.clipboard
      .writeText(JSON.stringify(securityMapData, null, 2))
      .then(() => {
        console.log("Security map data copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  };

  return (
    <>
      <Row>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3>Adjusted Target</h3>
          <button
            onClick={copySecurityMapData}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              color: "#6c757d",
            }}
            title="Copy security map data to clipboard"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
        <p>
          Detailed view of the adjustments made including restrictions and gains
          constrains
        </p>
      </Row>

      <Container>
        {Object.entries(explainData).map(([accountId, accountData]) => {
          const accountName =
            sharedData.accounts[accountId]?.account_name || accountId;

          const copyAccountSecurityMapData = () => {
            navigator.clipboard
              .writeText(JSON.stringify(accountData.securities_map, null, 2))
              .then(() => {
                console.log(
                  `Security map data for account ${accountId} copied to clipboard`,
                );
              })
              .catch((err) => {
                console.error("Failed to copy to clipboard:", err);
              });
          };

          return (
            <div key={accountId}>
              <Row>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <h4>
                    <AccountLink
                      accountId={accountId}
                      accountName={accountName}
                    />
                  </h4>
                  <button
                    onClick={copyAccountSecurityMapData}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      color: "#6c757d",
                    }}
                    title={`Copy security map data for ${accountName} to clipboard`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
              </Row>

              <Row className="mb-4">
                <Col md={12}>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Risk</th>
                        <th>Asset Class</th>
                        <th>Symbol</th>
                        <th>Constrained ($)</th>
                        <th>Target ($)</th>
                        <th>Adjusted Target ($)</th>
                        <th>Adjusted Allocation ($)</th>
                        <th>Target Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(accountData.securities_map)
                        .sort(([symbolA, securityA], [symbolB, securityB]) => {
                          // First sort by Risk
                          if (securityA.risk < securityB.risk) return -1;
                          if (securityA.risk > securityB.risk) return 1;

                          // If Risk is the same, sort by Asset Class
                          if (securityA.asset_class < securityB.asset_class)
                            return -1;
                          if (securityA.asset_class > securityB.asset_class)
                            return 1;

                          // If Risk and Asset Class are the same, sort by Symbol
                          if (symbolA < symbolB) return -1;
                          if (symbolA > symbolB) return 1;

                          return 0;
                        })
                        .map(([symbol, security]) => (
                          <tr key={symbol}>
                            <td>{security.risk}</td>
                            <td>{security.asset_class}</td>
                            <td>{symbol}</td>
                            <td>${security.constrained.toLocaleString()}</td>
                            <td>${security.target.toLocaleString()}</td>
                            <td>
                              $
                              {security.adjusted_target?.toLocaleString() ||
                                "—"}
                            </td>
                            <td>
                              $
                              {security.adjusted_allocation?.toLocaleString() ||
                                "—"}
                            </td>
                            <td>
                              {accountData.constrained_target[symbol] !==
                              undefined
                                ? accountData.constrained_target[symbol]
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      <tr className="fw-bold">
                        <td>—</td>
                        <td>—</td>
                        <td>Total</td>
                        <td>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce(
                              (sum, security) => sum + security.constrained,
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce((sum, security) => sum + security.target, 0)
                            .toLocaleString()}
                        </td>
                        <td>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce(
                              (sum, security) =>
                                sum + (security.adjusted_target || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td>
                          $
                          {Object.values(accountData.securities_map)
                            .reduce(
                              (sum, security) =>
                                sum + (security.adjusted_allocation || 0),
                              0,
                            )
                            .toLocaleString()}
                        </td>
                        <td>
                          {Object.values(accountData.constrained_target).reduce(
                            (sum, target) => sum + target,
                            0,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          );
        })}
      </Container>
    </>
  );
};

interface FinalTaxableTargetPortfolioStepProps {
  explainData: {
    final_target_portfolio: Record<
      string,
      {
        balance: number;
        target: Record<string, number>;
        buy_total: number | null;
        sell_total: number | null;
        trades: unknown | null;
        result_portfolio: unknown | null;
      }
    >;
    total_target_portfolio: {
      balance: number;
      target: Record<string, number>;
      buy_total: number | null;
      sell_total: number | null;
      trades: unknown | null;
      result_portfolio: unknown | null;
    };
  };
}

export const FinalTaxableTargetPortfolioStep = ({
  explainData,
}: FinalTaxableTargetPortfolioStepProps) => (
  <>
    <Row>
      <h3>Final Target Portfolio</h3>
    </Row>

    <Container>
      <Row className="mb-3">
        <Col>
          <h5>
            Total Portfolio Balance: $
            {explainData.total_target_portfolio.balance.toLocaleString()}
          </h5>
        </Col>
      </Row>

      {Object.entries(explainData.final_target_portfolio).map(
        ([accountId, account]) => (
          <div key={accountId}>
            <Row>
              <h4>Account: {accountId}</h4>
              <h5>Balance: ${account.balance.toLocaleString()}</h5>
            </Row>

            <Row>
              <Col md={6}>
                <h5>Target Allocations</h5>
                <Table>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Target Weight</th>
                      <th>Target Value(Converted)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(account.target).map(
                      ([symbol, allocation]) => (
                        <tr key={symbol}>
                          <td>{symbol}</td>
                          <td>{allocation}</td>
                          <td>
                            ${(allocation * account.balance).toLocaleString()}
                          </td>
                        </tr>
                      ),
                    )}
                    <tr className="fw-bold">
                      <td>Total</td>
                      <td>
                        {Object.values(account.target).reduce(
                          (sum, allocation) => sum + allocation,
                          0,
                        )}
                      </td>
                      <td>${account.balance.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </div>
        ),
      )}

      <Row className="mt-4">
        <h4>Total Target Portfolio</h4>
        <Col md={8}>
          <Table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Target Weight (Converted)</th>
                <th>Target Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(explainData.total_target_portfolio.target).map(
                ([symbol, amount]) => (
                  <tr key={symbol}>
                    <td>{symbol}</td>
                    <td>
                      {amount / explainData.total_target_portfolio.balance}
                    </td>
                    <td>${amount.toLocaleString()}</td>
                  </tr>
                ),
              )}
              <tr>
                <td>Total</td>
                <td>N/A</td>
                <td>
                  ${explainData.total_target_portfolio.balance.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  </>
);

interface AccountDetailsDrawerProps {
  accounts: Record<string, Account>;
  householdModel: {
    primary: Record<string, number>;
    secondary: Record<string, Record<string, Record<string, unknown>>>;
  } | null;
  categoryMapping?: Record<string, Record<string, number>>;
}

const AccountDetailsDrawer: React.FC<AccountDetailsDrawerProps> = ({
  accounts,
  householdModel,
  categoryMapping,
}) => {
  const [showHouseholdDrawer, setShowHouseholdDrawer] = React.useState(false);
  const [showAccountsList, setShowAccountsList] = React.useState(false);
  const [showAccountDetails, setShowAccountDetails] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState<Account | null>(
    null,
  );

  const handleCloseHouseholdDrawer = () => setShowHouseholdDrawer(false);
  const handleShowHouseholdDrawer = () => setShowHouseholdDrawer(true);

  const handleCloseAccountsList = () => setShowAccountsList(false);
  const handleShowAccountsList = () => setShowAccountsList(true);

  const handleCloseAccountDetails = () => setShowAccountDetails(false);
  const handleShowAccountDetails = (account: Account) => {
    setSelectedAccount(account);
    setShowAccountDetails(true);
  };

  return (
    <>
      <>
        <Button
          variant="outline-primary"
          size="sm"
          style={{
            position: "fixed",
            right: "10px",
            top: "45%",
            transform: "rotate(90deg)",
            transformOrigin: "right",
            zIndex: 1000,
          }}
          onClick={handleShowHouseholdDrawer}
        >
          Household
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          style={{
            position: "fixed",
            right: "10px",
            top: "55%",
            transform: "rotate(90deg)",
            transformOrigin: "right",
            zIndex: 1000,
          }}
          onClick={handleShowAccountsList}
        >
          Accounts
        </Button>
      </>

      {/* Household drawer */}
      <Offcanvas
        show={showHouseholdDrawer}
        onHide={handleCloseHouseholdDrawer}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Household Model</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {householdModel ? (
            <>
              <h5>Primary Securities</h5>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Target Allocation</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(householdModel.primary).map(
                    ([symbol, allocation]) => (
                      <tr key={symbol}>
                        <td>{symbol}</td>
                        <td>{allocation}</td>
                      </tr>
                    ),
                  )}
                  <tr className="fw-bold">
                    <td>Total</td>
                    <td>
                      {Object.values(householdModel.primary).reduce(
                        (sum, allocation) => sum + allocation,
                        0,
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {Object.keys(householdModel.secondary).length > 0 && (
                <>
                  <h5>Security Groups</h5>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Primary</th>
                        <th>Secondary</th>
                        <th>Treatment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(householdModel.secondary).map(
                        ([symbol, mapping]) => {
                          const [primarySymbol, treatment] =
                            Object.entries(mapping)[0];
                          const treatmentType = Object.keys(treatment)[0];
                          return (
                            <tr key={symbol}>
                              <td>{primarySymbol}</td>
                              <td>{symbol}</td>
                              <td>{treatmentType}</td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </Table>
                </>
              )}

              {categoryMapping && Object.keys(categoryMapping).length > 0 && (
                <>
                  <h5>Category Mappings</h5>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Growth</th>
                        <th>Defensive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(categoryMapping).map(
                        ([category, allocations]) => (
                          <tr key={category}>
                            <td>{category}</td>
                            <td>{allocations.growth}</td>
                            <td>{allocations.defensive}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          ) : (
            <p>No household model available</p>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Accounts List drawer */}
      <Offcanvas
        show={showAccountsList}
        onHide={handleCloseAccountsList}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Accounts</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Type</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(accounts).map((account) => (
                <tr
                  key={account.account_id}
                  onClick={() => handleShowAccountDetails(account)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{account.account_name}</td>
                  <td>{account.settings.account_type}</td>
                  <td>${account.balance?.toLocaleString() ?? "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Account Details drawer */}
      <Offcanvas
        show={showAccountDetails}
        onHide={handleCloseAccountDetails}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {selectedAccount && (
              <AccountLink
                accountId={selectedAccount.account_id}
                accountName={selectedAccount.account_name}
              />
            )}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selectedAccount && (
            <>
              <Table striped bordered>
                <tbody>
                  <tr>
                    <td>Account ID</td>
                    <td>{selectedAccount.account_id}</td>
                  </tr>
                  <tr>
                    <td>Bridge Account ID</td>
                    <td>{selectedAccount.bridge_account_id}</td>
                  </tr>
                  <tr>
                    <td>Account Type</td>
                    <td>{selectedAccount.settings.account_type}</td>
                  </tr>
                  <tr>
                    <td>Tax Type</td>
                    <td>{selectedAccount.settings.tax_type}</td>
                  </tr>
                  <tr>
                    <td>Balance</td>
                    <td>
                      ${selectedAccount.balance?.toLocaleString() ?? "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Cash Balance</td>
                    <td>
                      ${selectedAccount.cash_balance?.toLocaleString() ?? "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Risk Score</td>
                    <td>{selectedAccount.risk_score ?? "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Rebalance Type</td>
                    <td>{selectedAccount.rebalance_type}</td>
                  </tr>
                  <tr>
                    <td>Is Rebalance Account</td>
                    <td>
                      {selectedAccount.is_rebalance_account ? "Yes" : "No"}
                    </td>
                  </tr>
                  <tr>
                    <td>Capital Gains (Short Term)</td>
                    <td>
                      {selectedAccount.settings.gains_budget_st
                        ? selectedAccount.settings.gains_budget_st
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Capital Gains (Long Term)</td>
                    <td>
                      {selectedAccount.settings.gains_budget_lt
                        ? selectedAccount.settings.gains_budget_lt
                        : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <h5>Cash Settings</h5>
              <Table striped bordered>
                <tbody>
                  <tr>
                    <td>Target Cash Type</td>
                    <td>{selectedAccount.cash_settings.target_cash_type}</td>
                  </tr>
                  <tr>
                    <td>Target Cash Weight</td>
                    <td>
                      {selectedAccount.cash_settings.target_cash_weight !== null
                        ? `${selectedAccount.cash_settings.target_cash_weight}%`
                        : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td>Target Cash Dollar</td>
                    <td>
                      ${selectedAccount.cash_settings.target_cash_dollar ?? "—"}
                    </td>
                  </tr>
                  <tr>
                    <td>Min Cash Settings</td>
                    <td>
                      {selectedAccount.cash_settings
                        .minimum_cash_trigger_dollar !== null
                        ? `$${selectedAccount.cash_settings.minimum_cash_trigger_dollar}`
                        : "—"}{" "}
                      {
                        selectedAccount.cash_settings
                          .minimum_cash_trigger_condition
                      }{" "}
                      {selectedAccount.cash_settings
                        .minimum_cash_trigger_weight !== null
                        ? `${selectedAccount.cash_settings.minimum_cash_trigger_weight}%`
                        : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td>Max Cash Settings</td>
                    <td>
                      {selectedAccount.cash_settings
                        .maximum_cash_trigger_dollar !== null
                        ? `$${selectedAccount.cash_settings.maximum_cash_trigger_dollar}`
                        : "—"}{" "}
                      {
                        selectedAccount.cash_settings
                          .maximum_cash_trigger_condition
                      }{" "}
                      {selectedAccount.cash_settings
                        .maximum_cash_trigger_weight !== null
                        ? `${selectedAccount.cash_settings.maximum_cash_trigger_weight}%`
                        : "—"}
                    </td>
                  </tr>
                </tbody>
              </Table>

              {selectedAccount.model && (
                <>
                  <h5>Model Portfolio</h5>
                  <Table striped bordered>
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Target Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedAccount.model.primary).map(
                        ([symbol, allocation]) => (
                          <tr key={symbol}>
                            <td>{symbol}</td>
                            <td>{allocation}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>

                  {Object.keys(selectedAccount.security_restrictions).length >
                    0 && (
                    <>
                      <h5>Security Restrictions</h5>
                      <Table striped bordered>
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th>Treatment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(
                            selectedAccount.security_restrictions,
                          ).map(([symbol, treatment]) => (
                            <tr key={symbol}>
                              <td>{symbol}</td>
                              <td>{String(treatment)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  {Object.keys(selectedAccount.model.secondary).length > 0 && (
                    <>
                      <h5>Secondary Securities</h5>
                      <Table striped bordered>
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th>Secondary To</th>
                            <th>Treatment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(selectedAccount.model.secondary).map(
                            ([secondarySymbol, mapping]) => {
                              const [primarySymbol, treatment] =
                                Object.entries(mapping)[0];
                              const treatmentType = Object.keys(treatment)[0];
                              return (
                                <tr key={secondarySymbol}>
                                  <td>{secondarySymbol}</td>
                                  <td>{primarySymbol}</td>
                                  <td>{treatmentType}</td>
                                </tr>
                              );
                            },
                          )}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

const RebalanceHouseholdExplain = () => {
  const { rebalance } = useContext(RebalanceContext);

  const {
    isPending,
    isError,
    data: explainData,
  } = useAuthenticatedFetch<
    UnpackResponse<RebalancesController["getRebalanceExplain"]>
  >(`/rebalances/${rebalance?.id}/explain`);

  if (isPending) return <Loading />;
  if (isError) return <div>Error loading rebalance explanation</div>;

  const orderedSteps = explainData?.data.toSorted(
    (a, b) => a.explainId - b.explainId,
  );

  const sharedData: SharedRebalanceData = {
    accounts: {},
    householdModel: null,
    constrainedAccounts: {},
    trades: {},
  };

  orderedSteps?.forEach((step) => {
    switch (step.stepName) {
      case "inputs":
        sharedData.accounts = step.explainData.accounts;
        sharedData.householdModel = step.explainData.household_model;
        break;

      case "constrained_accounts":
        sharedData.constrainedAccounts = step.explainData;
        break;

      case "final_taxable_target_portfolio":
        // Extract trades data from final_target_portfolio
        if (step.explainData.final_target_portfolio) {
          Object.entries(step.explainData.final_target_portfolio).forEach(
            ([accountId, accountData]) => {
              const typedAccountData = accountData as {
                trades?: Record<
                  string,
                  {
                    shares: number;
                    price: number;
                    realized_gain_loss_st: number;
                    realized_gain_loss_lt: number;
                    trade_weight: number | null;
                    remaining_trade_value: number | null;
                    remaining_shares_available_to_sell: number | null;
                  }
                >;
              };
              if (typedAccountData.trades) {
                if (!sharedData.trades) {
                  sharedData.trades = {};
                }
                sharedData.trades[accountId] = typedAccountData.trades;
              }
            },
          );
        }
        break;

      default:
        break;
    }
  });

  return (
    <div className="rebalance-explain" data-page="explain">
      {orderedSteps?.map((step) => {
        switch (step.stepName) {
          case "inputs":
            return (
              <RebalanceRequestExplainStep
                key={step.explainId}
                explainData={step.explainData}
              />
            );
          case "target_portfolio":
            return (
              <TargetPortfolioRow
                key={step.explainId}
                explainData={step.explainData}
                sharedData={sharedData}
              />
            );
          case "target_risk_allocations":
            return (
              <TargetRiskAllocationRow
                key={step.explainId}
                sharedData={sharedData}
                explainData={step.explainData}
              />
            );
          case "model_cash":
            // Skip rendering model_cash step
            return null;
          case "constrained_accounts":
            return (
              <ConstrainedAccountsStep
                key={step.explainId}
                explainData={step.explainData}
                sharedData={sharedData}
              />
            );
          case "target_final":
            return (
              <TargetFinalExplainStep
                key={step.explainId}
                explainData={step.explainData}
                sharedData={sharedData}
              />
            );
          case "taxable_rebalance":
            return (
              <TaxableAccountRebalanceStep
                key={step.explainId}
                sharedData={sharedData}
                explainData={step.explainData}
              />
            );
          case "final_taxable_target_portfolio":
            return (
              <FinalTaxableTargetPortfolioStep
                key={step.explainId}
                explainData={step.explainData}
              />
            );
          default:
            return (
              <MissingStep
                key={step.explainId}
                stepName={step.stepName}
                explainData={step.explainData}
              />
            );
        }
      })}
    </div>
  );
};
export default RebalanceHouseholdExplain;
